/**
 * Each user's saved layout of a `BaseTable`: which columns are shown, their
 * order and widths, pinned columns, sort and density.
 *
 * Saved only when the user presses "Save as my default" — rearranging a table
 * changes this visit alone. Stored per user in SchedulerService
 * (`/user-preferences/table-layouts/{key}`, one row per user and table, for
 * every schedule), so it follows them to another computer, and
 * cached in this browser's localStorage so a table opens in the saved layout
 * at once instead of snapping into it when the request returns.
 *
 * Filters are deliberately NOT saved: a remembered filter silently hides rows
 * the next time the table opens, which reads as missing data.
 */
import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { request } from 'Features/auth/axios-client'
import { msalInstance } from 'Features/auth/msal-instance'

export type TableLayout = {
  v: 1
  columns?: {
    columnVisibilityModel?: GridColumnVisibilityModel
    orderedFields?: string[]
    dimensions?: Record<string, Record<string, unknown>>
  }
  sortModel?: GridSortModel
  pinnedLeft?: string[]
  density?: 'compact' | 'standard' | 'comfortable'
}

const url = (key: string) => `/user-preferences/table-layouts/${key}`

// Per user AND tenant: a shared machine must not show one planner another's
// layout, and a table's columns can differ between tenants.
const cacheKey = (key: string) => {
  const user = msalInstance.getActiveAccount()?.username ?? 'anon'
  const tenant = localStorage.getItem('tenantId') ?? ''
  return `tableLayout:${user.toLowerCase()}:${tenant}:${key}`
}

// Every localStorage access is guarded: it throws in some private modes, and
// a layout is a convenience that must never break the table.
const readCache = (key: string): TableLayout | null => {
  try {
    const raw = localStorage.getItem(cacheKey(key))
    return raw ? (JSON.parse(raw) as TableLayout) : null
  } catch {
    return null
  }
}

const writeCache = (key: string, layout: TableLayout | null) => {
  try {
    if (layout) localStorage.setItem(cacheKey(key), JSON.stringify(layout))
    else localStorage.removeItem(cacheKey(key))
  } catch {
    // ignore
  }
}

/**
 * The saved layout for `key` (undefined key = feature off), plus save and
 * reset. `cached` is this browser's copy, available synchronously on first
 * render; `server` is the stored copy once fetched (`null` = none saved).
 */
export const useTableLayout = (key?: string) => {
  const queryClient = useQueryClient()
  const query = useQuery(
    ['tableLayout', key],
    async () => {
      const res = await request({ url: url(key as string), method: 'GET' })
      const layout = (res?.data?.data ?? null) as TableLayout | null
      writeCache(key as string, layout)
      return layout
    },
    {
      enabled: Boolean(key),
      // Fetched once per table per session; this tab's own saves keep the
      // cache current, and refetching mid-edit would fight the user.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  )

  // Resolves true once the server has stored it. The browser copy is
  // written first either way, so this device keeps the layout even if the
  // request fails.
  const save = useCallback(
    async (layout: TableLayout): Promise<boolean> => {
      if (!key) return false
      writeCache(key, layout)
      // This session's copy too, or reopening the table would apply the
      // layout fetched before this save.
      queryClient.setQueryData(['tableLayout', key], layout)
      try {
        await request({ url: url(key), method: 'PUT', data: { layout } })
        return true
      } catch (e) {
        console.warn('Could not save table layout', key, e)
        return false
      }
    },
    [key, queryClient]
  )

  const reset = useCallback(() => {
    if (!key) return
    writeCache(key, null)
    queryClient.setQueryData(['tableLayout', key], null)
    request({ url: url(key), method: 'DELETE' }).catch((e) =>
      console.warn('Could not reset table layout', key, e)
    )
  }, [key, queryClient])

  return {
    cached: key ? readCache(key) : null,
    server: query.isSuccess ? (query.data ?? null) : undefined,
    save,
    reset,
  }
}
